// Shared types for the COI template editor.
//
// Field surface mirrors lodestar's `coi-template-editor.tsx` (Ana v1.2).
// Logrock additions: `policyId` (the editor scopes to one policy at a time),
// `additionalInsured` and `waiverOfSubrogation` (legacy flags consumed by
// `server/coi-generator.ts`).
//
// On send, the editor TRANSLATES this shape to the body the existing
// `POST /api/coi/preview` accepts (cert holder + flags + description +
// coverageLimits/agency/insured/carrierNaic). RunCOIDialog's call signature
// is unaffected.

import { z } from "zod";
import type { PolicyVehicle } from "./schema";
import { formatVehicleCoiLine } from "./coi-vehicles";

export interface CoiInsurer {
  name: string;
  naic: string;
}

// CSR-1791: validates each persisted lossPayeeVehicles element (mirrors the
// PolicyVehicle interface in schema.ts). All fields optional — a snapshot may
// carry as much or as little as the form captured.
const policyVehicleSnapshotSchema = z.object({
  vin: z.string().optional(),
  year: z.union([z.number(), z.string()]).optional(),
  make: z.string().optional(),
  model: z.string().optional(),
  truckType: z.string().optional(),
  value: z.string().optional(),
  deductibleComp: z.string().optional(),
  deductibleCollision: z.string().optional(),
  databaseId: z.string().optional(),
  addedAt: z.string().optional(),
  removedAt: z.string().nullable().optional(),
});

// CSR-1791: full snapshot of the Send COI modal's form state, persisted on each
// coi_issuances fan-out row so a "Resend COI" can restore the original send
// *configuration* (the COI itself is always regenerated from current policy
// data). Mirrors SendCOIDialog's useState fields one-for-one.
export const coiSendConfigSchema = z.object({
  certificateHolder: z.string(),
  selectedHolderId: z.string().nullable(),
  holderAddress: z.string(),
  holderCity: z.string(),
  holderState: z.string(),
  holderZip: z.string(),
  recipients: z.array(z.string()),
  sendCopyToInsured: z.boolean(),
  additionalInfo: z.array(z.string()),
  selectedVehicleIds: z.array(z.string()),
  selectedDriverIds: z.array(z.string()),
  selectedTrailerIds: z.array(z.string()),
  specialVerbiage: z.string(),
  additionalInsuredName: z.string(),
  additionalInsuredCoverageIds: z.array(z.string()),
  additionalInsuredSpecialVerbiage: z.string().nullable(),
  lossPayeeId: z.string().nullable(),
  lossPayeeName: z.string(),
  lossPayeeCoverageIds: z.array(z.string()),
  lossPayeeVehicles: z.array(policyVehicleSnapshotSchema),
  lossPayeeSpecialVerbiage: z.string().nullable(),
  waiverOfSubrogationId: z.string().nullable(),
  waiverOfSubrogationName: z.string(),
  waiverOfSubrogationCoverageIds: z.array(z.string()),
  waiverOfSubrogationSpecialVerbiage: z.string().nullable(),
});

export type CoiSendConfig = z.infer<typeof coiSendConfigSchema>;

// CSR-778 — per-policy snapshot persisted on `coi_issuances.coverage_limits`
// as the only durable record of which policies were combined onto a COI.
// The single-policy `policy.coverages` snapshot still lives at the root of
// the jsonb (back-compat for any downstream consumer that reads
// `coverageLimits.cargo`, etc.); `coiPolicies` is additive.
export interface CoiIssuancePolicySnapshot {
  policyId: string;
  policyNumber: string;
  carrier: string | null;
  carrierNaic: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  coverages: Record<string, unknown>;
  insurerLetter: "A" | "B" | "C" | "D" | "E" | "F";
}

export interface CoiGeneralLiabilityBlock {
  insurerLetter: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  occurrence: boolean;
  claimsMade: boolean;
  eachOccurrence: string;
  fireDamage: string;
  medExp: string;
  personalInjury: string;
  generalAggregate: string;
  productsCompOp: string;
}

export interface CoiAutoLiabilityBlock {
  insurerLetter: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  anyAuto: boolean;
  ownedAutos: boolean;
  hiredAutos: boolean;
  scheduledAutos: boolean;
  nonOwnedAutos: boolean;
  combinedSingleLimit: string;
  bodilyInjuryPerson: string;
  bodilyInjuryAccident: string;
  propertyDamage: string;
  // PIP takes precedence over Basic PIP; the selected limit shares the AL rectangle with NTL.
  pipLimit?: string;
  basicPipLimit?: string;
  nonTruckingLiabilityLimit?: string;
}

export const EMPTY_AUTO_LIABILITY: CoiAutoLiabilityBlock = {
  insurerLetter: "",
  policyNumber: "",
  effectiveDate: "",
  expirationDate: "",
  anyAuto: false,
  ownedAutos: false,
  hiredAutos: false,
  scheduledAutos: false,
  nonOwnedAutos: false,
  combinedSingleLimit: "",
  bodilyInjuryPerson: "",
  bodilyInjuryAccident: "",
  propertyDamage: "",
  nonTruckingLiabilityLimit: "",
};

export interface CoiUmbrellaBlock {
  insurerLetter: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  umbrella: boolean;
  excess: boolean;
  occurrence: boolean;
  eachOccurrence: string;
  aggregate: string;
}

export interface CoiWorkersCompBlock {
  insurerLetter: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  // PER STATUTE when true, OTHER when false (ACORD 25 statutory-limit boxes).
  perStatute: boolean;
  // "Y"/"N" for the ANY PROPRIETOR/PARTNER/OFFICER EXCLUDED? box.
  proprietorExcluded: string;
  eachAccident: string;
  diseaseEmployee: string;
  diseasePolicyLimit: string;
}

export interface CoiOtherCoverageRow {
  insurerLetter: string;
  description: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  limit: string;
  deductible: string;
  // Optional pre-formatted text for the LIMITS column. When set, it overrides
  // the default `Limit: …, Deductible: …` formatting in coiPdf.ts. Used for
  // coverages like Physical Damage where the LIMITS column shows comp/coll
  // deductibles instead of a single limit/deductible pair.
  limitDisplay?: string;
  // CSR-1062: per-row ADDL INSD flag. True when the AI is linked to THIS
  // specific OTHER coverage. The PDF writer draws "Y" per row instead of
  // relying on the single OtherPolicy AcroForm widget (which is one
  // fixed-position field and cannot represent N-row state).
  additionalInsured?: boolean;
  // CSR-1101: per-row SUBR WVD flag. True when a Waiver of Subrogation covers
  // THIS specific OTHER coverage. Sibling of `additionalInsured` — the PDF
  // writer draws "Y" per row at the OTHER-section SUBR WVD column, since the
  // single OtherPolicy SubrogationWaived AcroForm widget cannot represent the
  // up-to-3 hand-drawn OTHER sub-rows.
  subrogationWaived?: boolean;
  // CSR-1501: per-row ADDL INSD flag set when a Loss Payee is linked to THIS
  // specific OTHER coverage (e.g. a lienholder on a financed vehicle linked to
  // Physical Damage). Sibling of `additionalInsured` — the PDF writer draws "Y"
  // in the OTHER-section ADDL INSD column. A Loss Payee never marks SUBR WVD.
  lossPayee?: boolean;
}

export interface CoiCertificateHolder {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

// CSR-741: per-coverage Additional Insured Y/N. Per the COI Field Mapping/Rules
// doc (R8 surrounding context), the ADDL INSD column on each coverage row is
// conditional on whether the AI is linked to that specific coverage. When
// `additionalInsured` is true and `additionalInsuredCoverages` is undefined,
// the legacy "apply to every row" behavior holds (back-compat). When
// `additionalInsuredCoverages` is provided, only the listed sections render
// `Y` and the rest render `N`.
export interface CoiAdditionalInsuredCoverages {
  generalLiability?: boolean;
  autoLiability?: boolean;
  umbrella?: boolean;
  // The ACORD 25 form has a single ADDL INSD field for the OTHER section
  // (covers Cargo / Physical Damage / Trailer Interchange / etc. as one
  // bucket). Set true when the AI is linked to any of those.
  otherPolicy?: boolean;
}

// CSR-1101: per-coverage Waiver of Subrogation Y/N for the SUBR WVD column.
// Parallel to `CoiAdditionalInsuredCoverages` but with two differences: it
// includes `workersComp` (the WC row has a SUBR WVD AcroForm field; it has no
// ADDL INSD field) and it has NO `otherPolicy` key — SUBR WVD is marked on
// STANDARD coverage rows only (GL / Auto / Excess-Umbrella / WC). When this map
// is absent the PDF writer falls back to the flat `waiverOfSubrogation` boolean
// for all standard rows (back-compat); when present it supersedes the boolean.
export interface CoiWaiverOfSubrogationCoverages {
  generalLiability?: boolean;
  autoLiability?: boolean;
  umbrella?: boolean;
  workersComp?: boolean;
}

// CSR-741 (R8): Default Additional Insured / Loss Payee statements that
// belong in the Description of Operations free-text box. Special Verbiage
// on the entity overrides these; the caller supplies its own string in
// that case.
export const ADDITIONAL_INSURED_DEFAULT = (name: string): string =>
  `${name} is listed as an Additional Insured.`;
export const LOSS_PAYEE_DEFAULT = (name: string): string =>
  `${name} is listed as a Loss Payee.`;
export const ADDITIONAL_INSURED_AND_LOSS_PAYEE_DEFAULT = (name: string): string =>
  `${name} is listed as an Additional Insured and Loss Payee.`;
// CSR-1101: default Waiver of Subrogation statement. Reads "Additional Insured"
// (no "an") with a trailing period, matching the AI/LP convention (OQ-3).
export const WAIVER_OF_SUBROGATION_DEFAULT = (name: string): string =>
  `${name} is listed as Additional Insured with Waiver of Subrogation.`;

export const formatCoiDate = (date: Date): string =>
  date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

// CSR-1101: build the Description of Operations line for a Waiver of
// Subrogation. Sibling of `buildEntityRoleStatement` (kept separate so the AI/LP
// precedence is not regressed). Special Verbiage overrides the default entirely.
// The AI+WoS combined-line suppression (OQ-1) is handled by the caller
// (SendCOIDialog), not here.
export function buildWaiverOfSubrogationStatement(input: {
  name: string;
  specialVerbiage?: string | null;
}): string {
  const name = (input.name ?? "").trim();
  if (!name) return "";
  const sv = (input.specialVerbiage ?? "").trim();
  if (sv) return sv;
  return WAIVER_OF_SUBROGATION_DEFAULT(name);
}

// CSR-741 (R8): Build the Description of Operations line for a single
// entity that may be an Additional Insured, a Loss Payee, or both.
// Special Verbiage overrides the default phrasing entirely. When the same
// entity is both AI and LP and neither has a Special Verbiage string set,
// emit the combined "Additional Insured and Loss Payee" line.
export interface CoiEntityRoleStatementInput {
  name: string;
  additionalInsured?: boolean;
  lossPayee?: boolean;
  // CSR-792 — when present and the entity is LP-only with no specialVerbiage,
  // render the vehicle-aware variant: `${name} is listed as Loss Payee for
  // ${formatVehicleCoiLine(v1)}, ${formatVehicleCoiLine(v2)}, ....`. Vehicles
  // are expected to be already filtered for `!removedAt` by the caller.
  // Combined AI+LP case ignores this field in v1 (out_of_scope per spec).
  vehicles?: PolicyVehicle[];
  vehicleLabels?: string[];
  specialVerbiage?: string | null;
}

export function buildEntityRoleStatement(input: CoiEntityRoleStatementInput): string {
  const name = (input.name ?? "").trim();
  if (!name) return "";
  // Priority 1 — special verbiage replaces the default text entirely.
  const sv = (input.specialVerbiage ?? "").trim();
  if (sv) return sv;
  if (input.additionalInsured && input.lossPayee) {
    // CSR-792 v1: combined statement ignores vehicles. The combined case
    // is rare and the prototype/Linear doc do not specify a vehicle-aware
    // variant for it.
    return ADDITIONAL_INSURED_AND_LOSS_PAYEE_DEFAULT(name);
  }
  if (input.additionalInsured) return ADDITIONAL_INSURED_DEFAULT(name);
  if (input.lossPayee) {
    // Priority 2 — LP-only with vehicles attached.
    const lines = (input.vehicleLabels ?? (input.vehicles ?? []).map((v) => formatVehicleCoiLine(v)))
      .filter((s) => s.length > 0);
    if (lines.length > 0) {
      return `${name} is listed as Loss Payee for ${lines.join(", ")}.`;
    }
    // Priority 3 — default LP statement.
    return LOSS_PAYEE_DEFAULT(name);
  }
  return "";
}

// CSR-2163 — the one place the Description-of-Operations entity lines are
// composed, so the manual send, the saved-template send, and every programmatic
// path emit identical text.
//
// Composition used to be split: the Additional Insured and Waiver of
// Subrogation lines were built in the AMS send dialog and arrived here as
// opaque pre-baked text, while the Loss Payee line was built server-side. Only
// the dialog ever ran, so any caller that set `additionalInsured` without going
// through it produced a certificate with ADDL INSD "Y" and nobody named
// (CSR-2160) — the same contradiction CSR-2158 shipped to a real holder.
//
// MIGRATION (CSR-2163 step 1): each line is composed only when its NAME is
// supplied. A caller still baking sentences into the description sends no names,
// gets no composed lines, and is unaffected. That makes AMS and the service
// independently deployable — without it, the window where both compose prints
// every line twice.
export interface CoiEntityStatementsInput {
  // Text the caller has ALREADY put in the Description of Operations. Any
  // statement composed below that is already present there as a whole segment
  // is dropped rather than appended again.
  //
  // This is not defensive programming, it is a live migration hazard: saved COI
  // templates (CSR-1117) persist a Description of Operations, and every one
  // stored before composition moved server-side has the Additional Insured
  // sentence BAKED INTO it. Composing from the name on top of that would print
  // the sentence twice on the next send from that template.
  existingDescription?: string | null;
  additionalInsuredName?: string | null;
  additionalInsuredSpecialVerbiage?: string | null;
  waiverOfSubrogationName?: string | null;
  waiverOfSubrogationSpecialVerbiage?: string | null;
  lossPayeeName?: string | null;
  lossPayeeSpecialVerbiage?: string | null;
  lossPayeeVehicles?: PolicyVehicle[];
  lossPayeeVehicleLabels?: string[];
}

// CSR-1101 (OQ-1): when one entity is BOTH an Additional Insured and a Waiver of
// Subrogation, the WoS line subsumes the standalone AI line — it already names
// the entity as an additional insured, so emitting both says the same thing
// twice. "Same entity" is normalized-name equality, matching the rule this
// replaces in `SendCOIDialog`.
function isSameEntity(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function composeEntityRoleStatements(input: CoiEntityStatementsInput): string[] {
  const aiName = (input.additionalInsuredName ?? "").trim();
  const wosName = (input.waiverOfSubrogationName ?? "").trim();
  const lpName = (input.lossPayeeName ?? "").trim();

  const statements: string[] = [];

  const aiSubsumedByWos = aiName.length > 0 && wosName.length > 0 && isSameEntity(aiName, wosName);

  if (aiName && !aiSubsumedByWos) {
    const line = buildEntityRoleStatement({
      name: aiName,
      additionalInsured: true,
      specialVerbiage: input.additionalInsuredSpecialVerbiage,
    });
    if (line) statements.push(line);
  }

  if (wosName) {
    const line = buildWaiverOfSubrogationStatement({
      name: wosName,
      specialVerbiage: input.waiverOfSubrogationSpecialVerbiage,
    });
    if (line) statements.push(line);
  }

  if (lpName) {
    const line = buildEntityRoleStatement({
      name: lpName,
      lossPayee: true,
      vehicles: input.lossPayeeVehicles,
      vehicleLabels: input.lossPayeeVehicleLabels,
      specialVerbiage: input.lossPayeeSpecialVerbiage,
    });
    if (line) statements.push(line);
  }

  // Whole-segment match only. A statement that merely appears INSIDE some
  // longer remark is left alone — the segments here are generated sentences,
  // and dropping one because a producer happened to quote it would silently
  // remove a line the certificate is supposed to carry.
  const existing = new Set(
    (input.existingDescription ?? "")
      .split(/\n{2,}/)
      .map((segment) => segment.trim())
      .filter(Boolean),
  );
  return statements.filter((statement) => !existing.has(statement.trim()));
}

export interface CoiTemplateData {
  policyId: string;
  date: string;

  // Producer / Agency
  producerName: string;
  producerAddress: string;
  producerCity: string;
  producerState: string;
  producerZip: string;
  producerContactName: string;
  producerPhone: string;
  producerFax: string;
  producerEmail: string;

  // Named Insured
  insuredName: string;
  insuredAddress: string;
  insuredCity: string;
  insuredState: string;
  insuredZip: string;

  // Insurers (A–F). Single-carrier policies seed a one-element array.
  insurers: CoiInsurer[];

  // Per-coverage blocks. GL / Auto / Umbrella / WC always render — an empty
  // block carries a falsy render gate (`insurerLetter: ""`) so the PDF writer
  // skips it. CSR-1451: Auto Liability is always present so the ACORD 25
  // section shows even when the policy carries no auto coverage, letting the
  // team fill in the Covered Autos checkboxes manually.
  generalLiability: CoiGeneralLiabilityBlock;
  autoLiability: CoiAutoLiabilityBlock;
  umbrellaLiability: CoiUmbrellaBlock;
  workersComp: CoiWorkersCompBlock;
  otherCoverages?: CoiOtherCoverageRow[];

  descriptionOfOperations: string;
  certificateHolder: CoiCertificateHolder;

  // Logrock additions for /api/coi/preview compat.
  additionalInsured: boolean;
  // CSR-741: per-coverage AI Y/N. Optional — when undefined, the legacy
  // "apply to every row" behavior holds (back-compat). When present, only
  // the listed sections render `Y` in the ADDL INSD column.
  additionalInsuredCoverages?: CoiAdditionalInsuredCoverages;
  waiverOfSubrogation: boolean;
  // CSR-1101: per-coverage SUBR WVD Y/N for STANDARD rows only. Optional — when
  // undefined, the legacy "apply the flat `waiverOfSubrogation` boolean to every
  // standard row" behavior holds (back-compat). When present, only the listed
  // standard sections render `Y` in the SUBR WVD column and it supersedes the
  // flat boolean.
  waiverOfSubrogationCoverages?: CoiWaiverOfSubrogationCoverages;
  // CSR-1501: per-coverage ADDL INSD Y/N contributed by a linked Loss Payee.
  // Parallel to `additionalInsuredCoverages` — a Loss Payee linked to a coverage
  // marks that coverage's ADDL INSD column "Y" (it never touches SUBR WVD).
  // Reuses the AI coverage shape since the relevant rows are identical.
  lossPayeeCoverages?: CoiAdditionalInsuredCoverages;
}
